import React, {
  useEffect,
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import APICall from "../Utility/APICall";
import { paths } from "../Utility/Constants";
import { useAuth } from "../Utility/AuthProvider";

export const BuildingContext = createContext();

export const BuildingProvider = ({ children }) => {
  const [allBuildings, setAllBuildings] = useState([]);
  const [isLoadingBuildings, setIsLoadingBuildings] = useState(false);
  const [buildingError, setBuildingError] = useState(null);

  const { jwt } = useAuth();

  // Caching mechanism for buildings
  const buildingCache = useRef({});
  const lastAllBuildingsFetchTime = useRef(0);
  const CACHE_LIFETIME = 5 * 60 * 1000; // 5 minutes

  // Fetch All Buildings Method
  const fetchAllBuildings = useCallback(
    async (forceRefresh = false) => {
      if (
        !forceRefresh &&
        Date.now() - lastAllBuildingsFetchTime.current < CACHE_LIFETIME &&
        allBuildings.length > 0 // Ensure we actually have data
      ) {
        console.log("Serving all buildings from cache.");
        return;
      }

      if (!jwt) {
        setBuildingError("Authentication token not available.");
        return;
      }
      setIsLoadingBuildings(true);
      setBuildingError(null);
      try {
        const response = await APICall(
          "GET",
          paths.building.fetch, // Assuming this fetches all buildings
          undefined,
          undefined,
          jwt
        );
        if (response.status === "success") {
          console.log("All Building Data Updated from API:", response.response);
          const fetchedBuildings = response.response || [];
          setAllBuildings(fetchedBuildings);
          lastAllBuildingsFetchTime.current = Date.now();

          fetchedBuildings.forEach((building) => {
            if (building.entity_id) {
              buildingCache.current[building.entity_id] = building;
            }
          });
        } else {
          setBuildingError(
            response.message || "Failed to fetch all buildings."
          );
          setAllBuildings([]);
        }
      } catch (error) {
        console.error("Error fetching all buildings:", error);
        setBuildingError(
          "An unexpected error occurred while fetching all buildings."
        );
        setAllBuildings([]);
      } finally {
        setIsLoadingBuildings(false);
      }
    },
    [jwt]

    //Removed `allBuildings.length`. The function is now stable
    // and only changes if the user logs in or out.
  );

  // Add/Update/Delete methods (similar to TenantContext, updating allBuildings and cache)
  // ... (You can add these methods later if you need to perform CUD operations via this context)

  // Reset Buildings Data
  const resetBuildingData = useCallback(() => {
    setAllBuildings([]);
    setBuildingError(null);
    buildingCache.current = {};
    lastAllBuildingsFetchTime.current = 0;
    console.log("BuildingContext: State has been reset due to logout.");
  }, []);

  // --- useEffect Hook for Initial Data Fetch ---
  useEffect(() => {
    if (jwt) {
      console.log("BuildingProvider: JWT found, fetching buildings.");
      fetchAllBuildings();
    } else {
      console.warn("JWT not available, delaying initial all building fetch.");
      resetBuildingData();
    }
  }, [fetchAllBuildings, jwt, resetBuildingData]);

  return (
    <BuildingContext.Provider
      value={{
        allBuildings,
        isLoadingBuildings,
        buildingError,
        fetchAllBuildings, // Expose for manual refresh
        resetBuildingData,
      }}
    >
      {children}
    </BuildingContext.Provider>
  );
};

export const useBuildings = () => {
  const context = useContext(BuildingContext);
  if (context === undefined) {
    throw new Error("useBuildings must be used within a BuildingProvider");
  }
  return context;
};
